// ReminderTab — reminder manager ported from Apps/Yawmi. Storage: aweni_yawmi_reminders.
// Islamic templates use citation strings only — no religious text (yawmi-vs-memoriz boundary).
import type { AweniContext } from '../../types';

interface Reminder {
  id: string; title: string; desc: string; category: string;
  time: string; repeat: 'once' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  days?: number[]; date?: string; enabled: boolean; lastTriggered?: string;
}

const STORAGE_KEY = 'aweni_yawmi_reminders';

const TEMPLATES: Array<{ titleKey: string; descKey: string; category: string; time: string; repeat: Reminder['repeat']; days?: number[] }> = [
  { titleKey: 'yawmi_reminders_tpl_friday_kahf_title', descKey: 'yawmi_reminders_tpl_friday_kahf_desc', category: 'islamic', time: '08:00', repeat: 'weekly', days: [5] },
  { titleKey: 'yawmi_reminders_tpl_monday_fasting_title', descKey: 'yawmi_reminders_tpl_monday_fasting_desc', category: 'islamic', time: '06:00', repeat: 'weekly', days: [1] },
  { titleKey: 'yawmi_reminders_tpl_thursday_fasting_title', descKey: 'yawmi_reminders_tpl_thursday_fasting_desc', category: 'islamic', time: '06:00', repeat: 'weekly', days: [4] },
  { titleKey: 'yawmi_reminders_tpl_last_third_title', descKey: 'yawmi_reminders_tpl_last_third_desc', category: 'islamic', time: '03:30', repeat: 'daily' },
  { titleKey: 'yawmi_reminders_tpl_duha_prayer_title', descKey: 'yawmi_reminders_tpl_duha_prayer_desc', category: 'islamic', time: '09:00', repeat: 'daily' },
  { titleKey: 'yawmi_reminders_tpl_call_parents_title', descKey: 'yawmi_reminders_tpl_call_parents_desc', category: 'social', time: '18:00', repeat: 'weekly', days: [5, 6] },
  { titleKey: 'yawmi_reminders_tpl_white_days_title', descKey: 'yawmi_reminders_tpl_white_days_desc', category: 'islamic', time: '05:30', repeat: 'monthly' },
];

export class ReminderTab {
  private el: HTMLElement;
  private reminders: Reminder[] = [];
  private checker: number | null = null;
  private subView: 'mine' | 'templates' | 'form' = 'mine';

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void {
    try { this.reminders = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { this.reminders = []; }
    this.render(ctx);
    this.checker = window.setInterval(() => this.check(ctx), 30000);
  }

  unmount(): void {
    if (this.checker !== null) { clearInterval(this.checker); this.checker = null; }
    this.el.innerHTML = '';
  }

  private tr(ctx: AweniContext, k: string, fb: string): string { return ctx.i18n.t(k) || fb; }
  private save(): void { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.reminders)); }

  private add(r: Omit<Reminder, 'id'>): void {
    this.reminders.push({ ...r, id: `rem_${Date.now()}` });
    this.save();
  }

  private toggle(id: string): void {
    const r = this.reminders.find(r => r.id === id);
    if (r) { r.enabled = !r.enabled; this.save(); }
  }

  private delete(id: string): void {
    this.reminders = this.reminders.filter(r => r.id !== id);
    this.save();
  }

  private check(ctx: AweniContext): void {
    const now = new Date();
    const [h, m] = [now.getHours(), now.getMinutes()];
    for (const r of this.reminders) {
      if (!r.enabled) continue;
      const [rh, rm] = r.time.split(':').map(Number);
      if (rh !== h || rm !== m) continue;
      const key = now.toISOString().slice(0, 16);
      if (r.lastTriggered === key) continue;
      r.lastTriggered = key;
      this.save();
      if (Notification.permission === 'granted') {
        new Notification(r.title, { body: r.desc || this.tr(ctx, 'yawmi_reminders_my', 'Reminder') });
      }
    }
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);

    if (this.subView === 'form') { this.renderForm(ctx); return; }
    if (this.subView === 'templates') { this.renderTemplates(ctx); return; }

    // Mine tab
    this.el.innerHTML = `
      <div>
        <div style="display:flex;gap:8px;margin-bottom:12px">
          <button class="rtab-btn" data-sub="mine" style="padding:6px 14px;border-radius:var(--ln-radius-full);
            background:var(--ln-accent);color:var(--ln-accent-text);border:none;cursor:pointer;font-size:.85rem">
            ${t('yawmi_reminders_my', 'My Reminders')}</button>
          <button class="rtab-btn" data-sub="templates" style="padding:6px 14px;border-radius:var(--ln-radius-full);
            background:var(--ln-bg-card);border:1px solid var(--ln-border);cursor:pointer;font-size:.85rem">
            ${t('yawmi_reminders_templates', 'Templates')}</button>
          <button id="rem-add-btn" style="margin-inline-start:auto;padding:7px 14px;background:var(--ln-accent);color:var(--ln-accent-text);
            border:none;border-radius:var(--ln-radius-full);cursor:pointer;font-size:.85rem;font-weight:700">
            + ${t('yawmi_reminders_add', 'Add')}</button>
        </div>
        ${this.reminders.map(r => `
          <div style="display:flex;align-items:flex-start;gap:10px;padding:12px;background:var(--ln-bg-card);
            border-radius:var(--ln-radius-md);margin-bottom:8px;opacity:${r.enabled ? '1' : '.55'}">
            <div style="flex:1;min-width:0">
              <div style="font-weight:600;font-size:.9rem">${r.title}</div>
              ${r.desc ? `<div style="font-size:.78rem;color:var(--ln-text-secondary);margin-top:2px">${r.desc}</div>` : ''}
              <div style="font-size:.75rem;color:var(--ln-text-secondary);margin-top:3px">${r.time} · ${t('yawmi_reminders_repeat_' + r.repeat, r.repeat)}</div>
            </div>
            <label style="cursor:pointer">
              <input type="checkbox" class="rem-toggle" data-id="${r.id}" ${r.enabled ? 'checked' : ''} style="width:16px;height:16px">
            </label>
            <button class="rem-del" data-id="${r.id}" style="padding:4px 8px;background:transparent;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);cursor:pointer;font-size:.75rem">×</button>
          </div>`).join('')}
        ${!this.reminders.length ? `<p style="color:var(--ln-text-secondary);text-align:center;padding:24px 0">${t('yawmi_reminders_empty', 'No reminders yet.')}</p>` : ''}
      </div>`;

    this.el.querySelectorAll<HTMLButtonElement>('.rtab-btn').forEach(btn => {
      btn.addEventListener('click', () => { this.subView = btn.dataset.sub as typeof this.subView; this.render(ctx); });
    });
    this.el.querySelector('#rem-add-btn')?.addEventListener('click', () => { this.subView = 'form'; this.render(ctx); });
    this.el.querySelectorAll<HTMLInputElement>('.rem-toggle').forEach(cb => {
      cb.addEventListener('change', () => { this.toggle(cb.dataset.id!); this.render(ctx); });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.rem-del').forEach(btn => {
      btn.addEventListener('click', () => {
        if (confirm(t('yawmi_reminders_delete_confirm', 'Delete this reminder?'))) { this.delete(btn.dataset.id!); this.render(ctx); }
      });
    });
  }

  private renderTemplates(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    this.el.innerHTML = `
      <div>
        <div style="display:flex;gap:8px;margin-bottom:12px">
          <button class="rtab-btn" data-sub="mine" style="padding:6px 14px;border-radius:var(--ln-radius-full);
            background:var(--ln-bg-card);border:1px solid var(--ln-border);cursor:pointer;font-size:.85rem">
            ${t('yawmi_reminders_my', 'My Reminders')}</button>
          <button class="rtab-btn" data-sub="templates" style="padding:6px 14px;border-radius:var(--ln-radius-full);
            background:var(--ln-accent);color:var(--ln-accent-text);border:none;cursor:pointer;font-size:.85rem">
            ${t('yawmi_reminders_templates', 'Templates')}</button>
        </div>
        ${TEMPLATES.map((tpl, i) => `
          <div style="display:flex;align-items:flex-start;gap:10px;padding:12px;background:var(--ln-bg-card);
            border-radius:var(--ln-radius-md);margin-bottom:8px">
            <div style="flex:1">
              <div style="font-weight:600;font-size:.9rem">${t(tpl.titleKey, tpl.titleKey)}</div>
              <div style="font-size:.78rem;color:var(--ln-text-secondary);margin-top:2px">${t(tpl.descKey, tpl.descKey)}</div>
              <div style="font-size:.75rem;color:var(--ln-text-secondary);margin-top:3px">${tpl.time} · ${t('yawmi_reminders_repeat_' + tpl.repeat, tpl.repeat)}</div>
            </div>
            <button class="rem-use-tpl" data-idx="${i}" style="padding:5px 10px;background:var(--ln-accent);color:var(--ln-accent-text);
              border:none;border-radius:var(--ln-radius-sm);cursor:pointer;font-size:.8rem;white-space:nowrap">
              + ${t('yawmi_reminders_add', 'Add')}</button>
          </div>`).join('')}
      </div>`;

    this.el.querySelectorAll<HTMLButtonElement>('.rtab-btn').forEach(btn => {
      btn.addEventListener('click', () => { this.subView = btn.dataset.sub as typeof this.subView; this.render(ctx); });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.rem-use-tpl').forEach(btn => {
      btn.addEventListener('click', () => {
        const tpl = TEMPLATES[parseInt(btn.dataset.idx!)];
        if (!tpl) return;
        this.add({
          title: ctx.i18n.t(tpl.titleKey) || tpl.titleKey,
          desc: ctx.i18n.t(tpl.descKey) || tpl.descKey,
          category: tpl.category, time: tpl.time, repeat: tpl.repeat,
          days: tpl.days, enabled: true,
        });
        this.subView = 'mine';
        this.render(ctx);
      });
    });
  }

  private renderForm(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    this.el.innerHTML = `
      <div style="background:var(--ln-bg-card);border-radius:var(--ln-radius-lg);padding:16px">
        <h3 style="margin:0 0 12px;font-size:1rem">${t('yawmi_reminders_new_title', 'New Reminder')}</h3>
        <div style="margin-bottom:10px">
          <label style="display:block;font-size:.85rem;margin-bottom:4px">${t('yawmi_reminders_title_label', 'Title')}</label>
          <input id="rem-title" type="text" placeholder="${t('yawmi_reminders_title_placeholder', 'What do you want to be reminded of?')}"
            style="padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);width:100%;font-size:.9rem">
        </div>
        <div style="margin-bottom:10px">
          <label style="display:block;font-size:.85rem;margin-bottom:4px">${t('yawmi_reminders_desc_label', 'Note (optional)')}</label>
          <textarea id="rem-desc" rows="2" placeholder="${t('yawmi_reminders_desc_placeholder', 'Add details...')}"
            style="padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);width:100%;font-size:.9rem;resize:vertical"></textarea>
        </div>
        <div style="display:flex;gap:10px;margin-bottom:10px">
          <div style="flex:1">
            <label style="display:block;font-size:.85rem;margin-bottom:4px">${t('yawmi_reminders_time_label', 'Time')}</label>
            <input id="rem-time" type="time" value="09:00" style="padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);width:100%">
          </div>
          <div style="flex:1">
            <label style="display:block;font-size:.85rem;margin-bottom:4px">${t('yawmi_reminders_repeat_label', 'Repeat')}</label>
            <select id="rem-repeat" style="padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);width:100%">
              ${(['once','daily','weekly','monthly','yearly'] as const).map(r =>
                `<option value="${r}">${t('yawmi_reminders_repeat_' + r, r)}</option>`).join('')}
            </select>
          </div>
        </div>
        <div style="display:flex;gap:10px;margin-top:14px">
          <button id="rem-save" style="flex:1;padding:10px;background:var(--ln-accent);color:var(--ln-accent-text);border:none;border-radius:var(--ln-radius-md);cursor:pointer;font-weight:700">
            ${t('yawmi_reminders_save', 'Save Reminder')}</button>
          <button id="rem-cancel" style="padding:10px 16px;background:transparent;border:1px solid var(--ln-border);border-radius:var(--ln-radius-md);cursor:pointer">
            ${t('yawmi_reminders_cancel', 'Cancel')}</button>
        </div>
      </div>`;

    this.el.querySelector('#rem-save')?.addEventListener('click', () => {
      const title = (this.el.querySelector<HTMLInputElement>('#rem-title'))?.value.trim() || '';
      const desc = (this.el.querySelector<HTMLTextAreaElement>('#rem-desc'))?.value.trim() || '';
      const time = (this.el.querySelector<HTMLInputElement>('#rem-time'))?.value || '09:00';
      const repeat = (this.el.querySelector<HTMLSelectElement>('#rem-repeat'))?.value as Reminder['repeat'] || 'once';
      if (!title) return;
      this.add({ title, desc, category: 'custom', time, repeat, enabled: true });
      this.subView = 'mine';
      this.render(ctx);
    });
    this.el.querySelector('#rem-cancel')?.addEventListener('click', () => { this.subView = 'mine'; this.render(ctx); });
  }
}
